/**
 * TrendInsightsFooter — Reusable footer showing CAGR and trend direction.
 *
 * Mirrors the "insights footer" from FI's trend charts (Revenue, EV, Invested Capital).
 */

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface Props {
  /** Array of numeric values in chronological order */
  values: (number | null | undefined)[];
  /** Label for the CAGR stat, e.g. "Revenue" */
  metricLabel: string;
  /** Number of years in the displayed window */
  yearsLabel?: string;
}

function calcCAGR(first: number, last: number, years: number): number | null {
  if (first <= 0 || last <= 0 || years <= 0) return null;
  return (Math.pow(last / first, 1 / years) - 1) * 100;
}

type TrendDir = 'up' | 'down' | 'flat';

function getTrendDirection(values: number[]): TrendDir {
  if (values.length < 2) return 'flat';
  // Simple linear regression slope
  const n = values.length;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (values[i] - yMean);
    den += (i - xMean) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  // Normalise slope relative to mean to get a percentage change per period
  const normSlope = yMean !== 0 ? (slope / Math.abs(yMean)) * 100 : 0;
  if (normSlope > 1) return 'up';
  if (normSlope < -1) return 'down';
  return 'flat';
}

const TREND_META: Record<TrendDir, { icon: typeof TrendingUp; label: string; color: string }> = {
  up: { icon: TrendingUp, label: 'Upward', color: 'text-emerald-600' },
  down: { icon: TrendingDown, label: 'Downward', color: 'text-red-600' },
  flat: { icon: Minus, label: 'Flat', color: 'text-neutral-500' },
};

export default function TrendInsightsFooter({ values, metricLabel, yearsLabel }: Props) {
  const valid = values.filter((v): v is number => v != null && !isNaN(v));
  if (valid.length < 2) return null;

  const first = valid[0];
  const last = valid[valid.length - 1];
  const years = valid.length - 1;
  const cagr = calcCAGR(first, last, years);
  const trend = getTrendDirection(valid);
  const { icon: TrendIcon, label: trendLabel, color: trendColor } = TREND_META[trend];

  return (
    <div className="flex items-center gap-6 mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-500">
      {cagr !== null && (
        <span>
          {yearsLabel ?? `${years}-Year`} {metricLabel} CAGR:{' '}
          <span className={`font-semibold ${cagr >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {cagr >= 0 ? '+' : ''}{cagr.toFixed(1)}%
          </span>
        </span>
      )}
      <span className="flex items-center gap-1">
        Trend: <TrendIcon className={`w-3.5 h-3.5 ${trendColor}`} />
        <span className={`font-semibold ${trendColor}`}>{trendLabel}</span>
      </span>
    </div>
  );
}
